import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight } from "lucide-react";
import { toast } from 'react-toastify';
import "../assets/css/SupportTicket.css";
import "../assets/css/Urls.css";
import CustomSelect from "../components/CustomSelect";
import { formatTicketPid } from "../utils/formatPid";
import { getProjectPid } from "../utils/sessionProject";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const STATUS_OPTIONS = ["All Status", "open", "processing", "closed"];
const STATUS_LABEL = { open: "Open", processing: "Processing", closed: "Closed" };
const STATUS_STYLE = {
  open:       { bg: "#eff6ff", color: "#2563eb" },
  processing: { bg: "#fff7ed", color: "#ea580c" },
  closed:     { bg: "#f1f5f9", color: "#64748b" },
};
const ITEMS_PER_PAGE = 20;

const SupportTicket = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const projectPid = getProjectPid();

  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNextPage: false, hasPrevPage: false });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef("");
  const statusRef = useRef("All Status");

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "" });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = useCallback(async (pg = 1, searchVal = "", statusVal = "All Status") => {
    setLoading(true);
    try {
      const body = { projectPid, page: pg, limit: ITEMS_PER_PAGE };
      if (searchVal.trim()) body.search = searchVal.trim();
      if (statusVal !== "All Status") body.status = statusVal;

      const res = await fetch(`${BASE_URL}/api/v1/support-tickets/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status) {
        setTickets(data.response?.items || []);
        setPagination(data.response?.pagination || {});
      }
    } catch {}
    setLoading(false);
  }, [token, projectPid]);

  // Page change — read latest search/status from refs, not state
  useEffect(() => {
    fetchTickets(page, searchRef.current, statusRef.current);
  }, [fetchTickets, page]);

  // Status filter — immediate, reset page
  useEffect(() => {
    statusRef.current = statusFilter;
    setPage(1);
    fetchTickets(1, searchRef.current, statusFilter);
  }, [statusFilter]);

  // Search — debounced 400ms
  const searchDebounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchTickets(1, val, statusRef.current);
    }, 400);
  };
const handleFormChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) { setFormError("Subject is required."); return; }
    if (!form.description.trim()) { setFormError("Description is required."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/api/v1/support-tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ projectPid, subject: form.subject.trim(), description: form.description.trim() }),
      });
      const data = await res.json();
      if (data.status) {
        const t = data.response;
        setShowModal(false);
        setForm({ subject: "", description: "" });
        setFormError("");
        toast.success("Support ticket created successfully!");
        navigate(`/support-ticket-chat/${t.ticketPid}`, { state: { ticket: t } });
      } else {
        setFormError(data.message || "Failed to create ticket.");
      }
    } catch {
      setFormError("Network error. Please try again.");
    }
    setSubmitting(false);
  };

  const closeModal = () => { setShowModal(false); setForm({ subject: "", description: "" }); setFormError(""); };

  const totalPages = pagination.totalPages || 1;

  return (
    <main className="app-wrapper st-page">
      <div className="container-fluid">

        {/* Toolbar */}
        <div className="st-toolbar">
          <div className="st-search-wrap">
            <i className="fa-solid fa-magnifying-glass st-search-icon" />
            <input
              type="text"
              className="form-control st-search"
              placeholder="Search by subject, ID or name..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="d-flex gap-2 align-items-center">
            <CustomSelect
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              options={STATUS_OPTIONS.map(s => ({ value: s, label: s === "All Status" ? "All Status" : STATUS_LABEL[s] }))}
            />
            <button className="btn btn-primary st-new-btn" onClick={() => setShowModal(true)}>
              + New Ticket
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="st-card">
          <div className="table-responsive">
            <table className="table st-table mb-0">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Created By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-4 text-muted">Loading...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-4 text-muted">No tickets found.</td></tr>
                ) : tickets.map((t) => {
                  const s = t.status;
                  const createdAt = t.createdAt ? new Date(t.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";
                  const createdBy = t.createdBy ? `${t.createdBy.firstName || ""} ${t.createdBy.lastName || ""}`.trim() : "";
                  return (
                    <tr key={t.ticketPid}>
                      <td><span className="st-ticket-id">{formatTicketPid(t.ticketPid)}</span></td>
                      <td className="st-subject">{t.subject}</td>
                      <td>
                        <span className="st-badge" style={{ background: STATUS_STYLE[s]?.bg, color: STATUS_STYLE[s]?.color }}>
                          {STATUS_LABEL[s] || s}
                        </span>
                      </td>
                      <td>{createdAt}</td>
                      <td>{createdBy}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="ph-view-btn"
                          onClick={() => navigate(`/support-ticket-chat/${t.ticketPid}`, { state: { ticket: t } })}
                        >
                          View <ChevronRight size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="st-footer">
            <span className="st-showing">
              {tickets.length === 0
                ? "Showing 0"
                : `Showing ${(page - 1) * ITEMS_PER_PAGE + 1}–${Math.min(page * ITEMS_PER_PAGE, pagination.total || 0)} of ${pagination.total || 0}`}
            </span>
            <div className="st-pagination">
              <button className="st-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                <i className="fa-solid fa-angle-left" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={`st-page-btn ${p === page ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="st-page-btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                <i className="fa-solid fa-angle-right" />
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* New Ticket Modal */}
      {showModal && (
        <div className="st-modal-overlay" onClick={closeModal}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-modal-header">
              <h6 className="mb-0 fw-700">New Ticket</h6>
              <button className="st-modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="st-modal-body">
              {formError && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{formError}</div>}
              <div className="mb-3">
                <label className="form-label">Subject <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="subject"
                  className="form-control"
                  placeholder="Enter ticket subject"
                  value={form.subject}
                  onChange={handleFormChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Description <span className="text-danger">*</span></label>
                <textarea
                  name="description"
                  className="form-control"
                  rows={4}
                  placeholder="Describe the issue..."
                  value={form.description}
                  onChange={handleFormChange}
                />
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Creating..." : "Create Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default SupportTicket;
